import { useEffect, useState, useMemo, type ChangeEvent, type ReactElement } from 'react'
import { useQueryState } from 'nuqs'
import { parseAsReportConfigId } from '@util'
import { isDefined, isNotDefined, isNotEmpty } from '@terros/common'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Group, Switch, Title } from '@mantine/core'
import {
  useIsManager,
  useProfile,
  useSafeCurrentWorkflow,
  useServerReportState,
  useTranslation,
  type ServerReportFilter,
} from '@front/model'
import { ChooseDates, FilterMultiTeam, NotReady, Page, TButton, WorkflowSelect } from '@components'
import { useTrendsData } from './trends/useTrendsData'
import { TrendsReport } from './trends/TrendsReport'
import { TrendsDisplayControls, TrendsIntervalControl } from './trends/TrendsDisplayControls'
import { toTrendsSortState } from './trends/ordering'
import { toSupportedTrendsGrouping, toTimeGrouping } from './trends/grouping'
import { useDisplayData } from './state/useDisplayData'
import { applyStatsDefaults, useStatsClientState, useTableRows } from './state'
import { SetGrouping } from './SetGrouping'
import { ReportViewsProvider, SavedReportViews, useReportViews } from './reportViews'
import { ReportLayoutSwitch } from './ReportLayoutSwitch'
import { ReportConfigsGrid } from './ReportConfigsGrid'
import { SaveReportButton } from './report/SaveReportButton'
import { FilterButton } from './filter/FilterButton'
import { DownloadCsv, StatsTable, ToggleStatsColumns } from './components'
import { ArchivedUsersSelect } from './ArchivedUsersSelect'

function StatsPage(): ReactElement {
  const { t } = useTranslation()

  const clientState = useStatsClientState('STATS_REPORT')
  const {
    filters,
    setFilters,
    sortState,
    selectedColumnIds: selectedColumns,
    setSelectedColumnIds: setSelectedColumns,
    pinSelf,
    setPinSelf,
    range,
    setRange,
    grouping,
    setGrouping,
    archivedUsersDisplay,
    setArchivedUsersDisplay,
    teamIds,
    setTeamIds,
    layout,
    setLayout,
  } = clientState

  const isTrends = layout === 'trends'
  const trendsGrouping = isTrends ? toSupportedTrendsGrouping(grouping) : grouping
  useEffect(() => {
    if (isTrends && !toTimeGrouping(grouping)) setGrouping(trendsGrouping)
  }, [grouping, isTrends, setGrouping, trendsGrouping])
  const trends = useTrendsData(clientState, isTrends)

  const filter = useMemo((): ServerReportFilter => {
    return {
      start: range?.[0]?.valueOf(),
      end: range?.[1]?.valueOf(),
      teamIds: isNotEmpty(teamIds) ? teamIds : undefined,
      grouping,
    }
  }, [range, teamIds, grouping])

  const trendsSortState = isTrends ? toTrendsSortState(sortState, trends.axis) : sortState
  const workflowId = useSafeCurrentWorkflow()?.workflow.id
  const serverState = useServerReportState({
    filter,
    columns: selectedColumns,
    hideSystemUsers: true,
    showArchived: archivedUsersDisplay === 'aggregated' ? undefined : archivedUsersDisplay === 'individual',
    workflowId,
    skipKpiFetch: isTrends,
  })
  const { refetch, isFetching, data, isLoading, error, visibleColumns, reportInput } = serverState
  const { isAdmin, user } = useProfile()
  const { isManager } = useIsManager()
  const canSeeReportMap = isAdmin || isManager

  const statRows = data?.format === 'table' ? data.rows : []
  const prefetchedTotalRow = data?.format === 'table' ? data.totalRow : undefined

  const { filtered, pinnedUserRow } = useDisplayData({
    data: statRows,
    sortState,
    filters,
    filterColumns: visibleColumns,
    pinnedUser: pinSelf ? user?.userId : undefined,
  })

  const { tableRows, totalRow } = useTableRows(filtered, visibleColumns, prefetchedTotalRow)

  const [activeViewId, setActiveViewId] = useQueryState('viewId', parseAsReportConfigId)
  const reportViews = useReportViews(clientState, activeViewId, (id) => setActiveViewId(id ?? null))
  const activeView = reportViews.views.find((view) => view.reportConfigId === activeViewId)
  const isEditingScheduledReport = activeView?.configType === 'scheduled'

  const isNotReady = isLoading || error || !data

  const hasMultipleReports = reportViews.views.length >= 2
  const [gridDismissed, setGridDismissed] = useState(false)

  const showReportGrid = hasMultipleReports && isNotDefined(activeViewId) && !gridDismissed

  const showBackToReports = hasMultipleReports && (isDefined(activeViewId) || gridDismissed)

  const startFreshReport = (): void => {
    applyStatsDefaults(clientState)
    setActiveViewId(null)
    setGridDismissed(true)
  }

  if (reportViews.isLoading) return <NotReady isLoading />

  if (showReportGrid) {
    return (
      <ReportViewsProvider value={reportViews}>
        <ReportConfigsGrid canSeeReportMap={canSeeReportMap} onDismissGrid={() => setGridDismissed(true)} />
      </ReportViewsProvider>
    )
  }

  return (
    <ReportViewsProvider value={reportViews}>
      <Page>
        <Page.Heading
          isFetching={isTrends ? trends.isFetching : isFetching}
          refetch={isTrends ? trends.refetch : refetch}
          metadata={{ title: t('Stats') }}
          title={
            <>
              <Title order={2}>{t('Stats')}</Title>
              {isNotEmpty(reportViews.views) && (
                <SavedReportViews
                  views={reportViews.views}
                  activeViewId={activeViewId}
                  onAddReport={startFreshReport}
                />
              )}
            </>
          }
          back={showBackToReports}
          handleGoBack={() => {
            setActiveViewId(null)
            setGridDismissed(false)
          }}
        >
          <Group>
            {canSeeReportMap && (
              <TButton component={Link} icon='map' to='/stats/map'>
                {t('Open Map')}
              </TButton>
            )}
            <ReportLayoutSwitch
              disabled={isEditingScheduledReport}
              layout={layout}
              setLayout={(nextLayout) => {
                if (nextLayout === 'trends') setGrouping(toSupportedTrendsGrouping(grouping))
                setLayout(nextLayout)
              }}
            />
            <SaveReportButton clientState={clientState} />
          </Group>
        </Page.Heading>

        <Page.FunctionBar>
          <Group>
            <ChooseDates range={range} setRange={setRange} />
            {isTrends && <TrendsIntervalControl clientState={clientState} />}
            <SetGrouping
              allowCustomFields={!isTrends}
              allowLocationGroupings
              allowTimeGroupings={!isTrends}
              grouping={trendsGrouping}
              setGrouping={setGrouping}
            />
            <WorkflowSelect />
            <ToggleStatsColumns selectedColumns={selectedColumns} setSelectedColumns={setSelectedColumns} />
            <FilterMultiTeam setTeams={setTeamIds} teamIds={teamIds} />
            {!isTrends && <FilterButton currentGrouping={grouping} filters={filters} setFilters={setFilters} />}
            {isTrends && (
              <TrendsDisplayControls
                clientState={clientState}
                metrics={trends.chartableColumns}
                sortState={trendsSortState}
              />
            )}
          </Group>
          <Group>
            {!isTrends && (
              <>
                {grouping.type === 'user' && (
                  <ArchivedUsersSelect value={archivedUsersDisplay} onChange={setArchivedUsersDisplay} />
                )}
                {grouping.type === 'user' && (
                  <Switch
                    checked={pinSelf}
                    label={t('Pin Yourself')}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setPinSelf(event.currentTarget.checked)}
                  />
                )}
                <DownloadCsv
                  columns={visibleColumns}
                  filters={filters}
                  grouping={grouping}
                  reportInput={reportInput}
                  rows={filtered}
                  totalRow={totalRow}
                />
              </>
            )}
          </Group>
        </Page.FunctionBar>

        <Page.Content>
          {isTrends && <TrendsReport clientState={clientState} sortState={trendsSortState} trends={trends} />}
          {!isTrends && isNotReady && (
            <NotReady data={data} error={error} id='stats' isLoading={reportViews.isLoading || isLoading} />
          )}
          {!isTrends && !isNotReady && (
            <StatsTable data={tableRows} pinnedUserRow={pinnedUserRow} totalRow={totalRow} tableState={clientState} />
          )}
        </Page.Content>
      </Page>
    </ReportViewsProvider>
  )
}

export const Route = createFileRoute('/stats/')({
  component: StatsPage,
})
